/**
 * sessionRestore — persists the user's pending freemium action across the
 * payment redirect so we can drop them back exactly where they left off.
 *
 * Stored key: 'infralytics_upgrade_intent'
 * Shape: { returnPath, featureName, pendingQuery?, pendingPostcode?,
 *          partialResponse?, timestamp }
 *
 * Stale entries (>2 hours) are auto-purged on read.
 */
const KEY = 'infralytics_upgrade_intent';
const TTL_MS = 2 * 60 * 60 * 1000;

export function saveUpgradeIntent(data) {
  if (typeof window === 'undefined') return;
  try {
    const payload = { ...data, timestamp: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch (e) {
    /* sessionStorage disabled — silently ignore */
  }
}

export function getUpgradeIntent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp || Date.now() - parsed.timestamp > TTL_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearUpgradeIntent() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Called by the /payments/success page after verifyOrder() resolves COMPLETED.
 * If an intent exists, navigates back to the original feature page with
 * the pending query/postcode injected as URL params so the target hook can
 * pick them up. Otherwise, sends the user to /profile.
 */
export function restoreAfterPayment(navigate) {
  const intent = getUpgradeIntent();
  if (!intent?.returnPath) {
    navigate('/profile');
    return false;
  }
  const params = new URLSearchParams();
  if (intent.pendingQuery) params.set('q', intent.pendingQuery);
  if (intent.pendingPostcode) params.set('pc', intent.pendingPostcode);
  params.set('restored', '1');
  const url = params.toString()
    ? `${intent.returnPath}?${params.toString()}`
    : intent.returnPath;
  clearUpgradeIntent();
  navigate(url);
  return true;
}
